import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Grid,
  ThemeProvider,
  createTheme,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { Country, State, City } from "country-state-city";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import InstituteTable from "./InstituteTable";

// Zoho Books-inspired theme
const theme = createTheme({
  typography: {
    fontFamily:
      '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2E7CF6",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F8F9FB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1F1F1F",
      secondary: "#6B7280",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            fontSize: "13px",
            "& fieldset": {
              borderColor: "#D1D5DB",
              borderRadius: "4px",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#3B82F6",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "6px 10px",
            height: "auto",
            color: "#1F1F1F",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "6px 14px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
  },
});

// Custom Label Component
const FieldLabel = ({ children, required = false, width = "140px" }) => (
  <Box
    sx={{
      minWidth: width,
      maxWidth: width,
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: 400,
      color: "#4B5563",
      pr: 2,
    }}
  >
    {children}
    {required && <span style={{ color: "#E85858", marginLeft: "2px" }}>*</span>}
  </Box>
);

// Form Field Component
const FormField = ({ children, sx = {} }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      mb: 1.5,
      ...sx,
    }}
  >
    {children}
  </Box>
);



const InstituteManagement = () => {
  // View state
  const [currentView, setCurrentView] = useState("list");

  // Table state
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  // Form state
  const [instituteName, setInstituteName] = useState("");
  const [instituteType, setInstituteType] = useState(null);
  const [universityName, setUniversityName] = useState("");
  const [currentInstituteTypes, setCurrentInstituteTypes] = useState([]);
  const [mainTab, setMainTab] = useState(0);
  const [countries, setCountries] = useState([]);
  const [allBackendStates, setAllBackendStates] = useState([]);
  const [billingStates, setBillingStates] = useState([]);
  const [billingCities, setBillingCities] = useState([]);
  const [globalUniversities, setGlobalUniversities] = useState([]);
  const [loadingUniversities, setLoadingUniversities] = useState(false);
  const [billingAttention, setBillingAttention] = useState("");
  const [billingCountry, setBillingCountry] = useState(null);
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingAddressLine2, setBillingAddressLine2] = useState("");
  const [billingCity, setBillingCity] = useState(null);
  const [billingState, setBillingState] = useState(null);
  const [billingPincode, setBillingPincode] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingFax, setBillingFax] = useState("");
  const [website, setWebsite] = useState("");
  const [gstinNo, setGstinNo] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [newInstituteType, setNewInstituteType] = useState("");
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentHoldTds, setStudentHoldTds] = useState("");
  const [instituteLogo, setInstituteLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch institutes
  useEffect(() => {
    if (currentView === "list") {
      fetchInstitutes();
    }
  }, [page, pageSize, searchTerm, currentView]);

  // Load institute types, countries and states from backend
  useEffect(() => {
    const fetchStateCountryLists = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/accounts/state_country_lists`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        const data = response.data;
        if (data.institute_types) {
          setCurrentInstituteTypes(data.institute_types);
        }
        if (data.country_list) {
          setCountries(data.country_list);
          // Set default country to India
          const india = data.country_list.find(
            (c) => c.country_name.toLowerCase() === "india"
          );
          if (india) {
            setBillingCountry(india);
          }
        }
        if (data.state_list) {
          setAllBackendStates(data.state_list);
        }
      } catch (error) {
        console.error("Error fetching state/country lists:", error);
      }
    };
    fetchStateCountryLists();
  }, []);

  const fetchInstitutes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/get-all-institutes`,
        {
          params: {
            page: page + 1,
            page_size: pageSize,
            search: searchTerm,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setInstitutes(response.data.institutes || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching institutes:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch institutes",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateNew = () => {
    setCurrentView("create");
  };

  const handleBackToList = () => {
    setCurrentView("list");
    handleReset();
  };

  // Form handlers
  const fetchGlobalUniversities = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return;
    setLoadingUniversities(true);
    try {
      const response = await axios.get(
        `http://universities.hipolabs.com/search?name=${encodeURIComponent(
          searchTerm
        )}`
      );
      const formattedUniversities = response.data.map((uni) => ({
        name: uni.name,
        type: "University",
        country: uni.country,
        website: uni.web_pages?.[0] || "",
        domain: uni.domains?.[0] || "",
        source: "global",
      }));
      setGlobalUniversities(formattedUniversities);
    } catch (error) {
      console.error("Error fetching universities:", error);
      setGlobalUniversities([]);
    } finally {
      setLoadingUniversities(false);
    }
  };

  // Filter states by selected country from backend data
  useEffect(() => {
    if (billingCountry) {
      const filtered = allBackendStates.filter(
        (s) => s.country_id === billingCountry.country_id
      );
      setBillingStates(filtered);
    } else {
      setBillingStates([]);
      setBillingState(null);
    }
  }, [billingCountry, allBackendStates]);

  // Load cities using country-state-city library (backend doesn't provide cities)
  useEffect(() => {
    if (billingCountry && billingState) {
      // Find ISO codes to look up cities
      const cscCountries = Country.getAllCountries();
      const cscCountry = cscCountries.find(
        (c) => c.name.toLowerCase() === billingCountry.country_name.toLowerCase()
      );
      if (cscCountry) {
        const cscStates = State.getStatesOfCountry(cscCountry.isoCode);
        const cscState = cscStates.find(
          (s) => s.name.toLowerCase() === billingState.state_name.toLowerCase()
        );
        if (cscState) {
          const cities = City.getCitiesOfState(cscCountry.isoCode, cscState.isoCode);
          setBillingCities(cities);
        } else {
          setBillingCities([]);
        }
      } else {
        setBillingCities([]);
      }
      setBillingCity(null);
    } else {
      setBillingCities([]);
      setBillingCity(null);
    }
  }, [billingState, billingCountry]);

  const handleInstituteNameChange = (event, newValue) => {
    if (!newValue) {
      setInstituteName("");
      return;
    }
    if (typeof newValue === "string") {
      setInstituteName(newValue);
      return;
    }
    setInstituteName(newValue.name);
    if (newValue.source === "global") {
      if (newValue.website) {
        setWebsite(newValue.website);
      }
      const country = countries.find(
        (c) => c.country_name.toLowerCase() === newValue.country?.toLowerCase()
      );
      if (country) {
        setBillingCountry(country);
      }
      setInstituteType("University");
    }
  };

  const handleCityChange = (event, newValue) => {
    if (!newValue) {
      setBillingCity(null);
      return;
    }
    if (typeof newValue === "string") {
      setBillingCity(newValue);
      return;
    }
    setBillingCity(newValue);
  };

  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewInstituteType("");
  };

  const handleCreateNewType = () => {
    if (newInstituteType.trim()) {
      setCurrentInstituteTypes([...currentInstituteTypes, newInstituteType]);
      setInstituteType(newInstituteType);
      handleCloseDialog();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const handleLogoChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  if (file.size > 1 * 1024 * 1024) {
    setSnackbar({
      open: true,
      message: "Logo must be less than 1MB",
      severity: "error",
    });
    e.target.value = "";
    return;
  }
  setInstituteLogo(file);
  setLogoPreview(URL.createObjectURL(file));
};
const handleRemoveLogo = () => {
  setInstituteLogo(null);
  setLogoPreview(null);
};
  const getPhoneCode = () => {
    if (billingCountry) {
      // Look up phone code from country-state-city library
      const cscCountries = Country.getAllCountries();
      const cscCountry = cscCountries.find(
        (c) => c.name.toLowerCase() === billingCountry.country_name.toLowerCase()
      );
      if (cscCountry && cscCountry.phonecode) {
        return `+${cscCountry.phonecode}`;
      }
    }
    return "";
  };

  const isInstituteNameValid = () => instituteName.trim() !== "";
  const isInstituteTypeValid = () => instituteType !== null && instituteType !== "";
  const isCountryValid = () => billingCountry !== null;
  const isStateValid = () => billingState !== null;
  const isAddressLine1Valid = () => billingAddressLine1.trim() !== "";
  const isCityValid = () => billingCity !== null && billingCity !== "";
  const isPincodeValid = () => billingPincode.trim() !== "";

  const isFormValid = () => {
    return (
      isInstituteNameValid() &&
      isInstituteTypeValid() &&
      isCountryValid() &&
      isStateValid() &&
      isAddressLine1Valid() &&
      isCityValid() &&
      isPincodeValid()
    );
  };

  const handleSubmit = async () => {
  setAttemptedSubmit(true);
  if (!isFormValid()) {
    setSnackbar({
      open: true,
      message: "Please fill in all required fields marked with *",
      severity: "error",
    });
    return;
  }
  setIsSubmitting(true);
  try {
    const phoneCode = getPhoneCode();
    const fullPhone = billingPhone ? `${phoneCode}-${billingPhone}` : "";
    const fullFax = billingFax ? `${phoneCode}-${billingFax}` : "";

    const formData = new FormData();
    formData.append("institute_name", instituteName);
    formData.append("institute_type", instituteType || "");
    formData.append("university_name", universityName || instituteName);
    formData.append("website", website);
    formData.append("gstin_no", gstinNo);
    formData.append("student_hold_tds", studentHoldTds ? parseInt(studentHoldTds, 10) : 0);
    formData.append(
      "billing_address",
      JSON.stringify({
        attention: billingAttention,
        address_line1: billingAddressLine1,
        address_line2: billingAddressLine2,
        city: typeof billingCity === "string" ? billingCity : billingCity?.name,
        state_id: billingState?.state_id,
        state_name: billingState?.state_name,
        country_id: billingCountry?.country_id,
        country_name: billingCountry?.country_name,
        pincode: billingPincode,
        phone: fullPhone,
        fax: fullFax,
      })
    );
    if (instituteLogo) {
      formData.append("institute_logo", instituteLogo);
    }

    await axios.post(`${BASE_URL}/accounts/institute`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });

    setSnackbar({
      open: true,
      message: "Institute created successfully!",
      severity: "success",
    });
    setTimeout(() => {
      handleBackToList();
      fetchInstitutes();
    }, 1500);
  } catch (error) {
    console.error("Error creating institute:", error);
    setSnackbar({
      open: true,
      message:
        error.response?.data?.message ||
        "Failed to create institute. Please try again.",
      severity: "error",
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const handleReset = () => {
    setInstituteName("");
    setInstituteType(null);
    setUniversityName("");
    setBillingAttention("");
    setStudentHoldTds("");
    // Reset country to India (default)
    const india = countries.find(
      (c) => c.country_name.toLowerCase() === "india"
    );
    setBillingCountry(india || null);
    setBillingAddressLine1("");
    setBillingAddressLine2("");
    setBillingCity(null);
    setBillingState(null);
    setBillingPincode("");
    setBillingPhone("");
    setBillingFax("");
    setWebsite("");
    setGstinNo("");
    setAttemptedSubmit(false);
    setInstituteLogo(null);
    setLogoPreview(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#F8F9FB",
          p: 3,
          pb: currentView === "create" ? 10 : 3,
        }}
      >
        <Box sx={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Header */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {currentView === "create" && (
                <IconButton
                  onClick={handleBackToList}
                  sx={{
                    color: "#3B82F6",
                    "&:hover": { backgroundColor: "#EFF6FF" },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <BusinessIcon sx={{ fontSize: 28, color: "#3B82F6" }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "#1F1F1F",
                  fontSize: "20px",
                }}
              >
                {currentView === "list" ? "Institutes" : "Create Institute"}
              </Typography>
            </Box>

            {currentView === "list" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{
                  backgroundColor: "#3B82F6",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                }}
              >
                Create New Institute
              </Button>
            )}
          </Box>
          {/* List View */}
          {currentView === "list" && (
            <InstituteTable
              institutes={institutes}
              loading={loading}
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={handleChangePage}
              onPageSizeChange={handleChangeRowsPerPage}
              onSearch={handleSearch}
              onUpdate={fetchInstitutes} // Add this prop
            />
          )}
          {/* Create Form View */}
          {currentView === "create" && (
            <Paper sx={{ p: 3 }}>
              {/* Form content - same as before, just keeping it clean */}
              <Box sx={{ mb: 3 }}>
                <FormField>
                  <FieldLabel required>Institute Name</FieldLabel>
                  <Autocomplete
                    freeSolo
                    value={instituteName}
                    onChange={handleInstituteNameChange}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setInstituteName(newInputValue);
                        if (newInputValue.length >= 2) {
                          fetchGlobalUniversities(newInputValue);
                        }
                      }
                    }}
                    options={[...globalUniversities]}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option?.name || "";
                    }}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options.slice(0, 50);
                      return options
                        .filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        )
                        .slice(0, 50);
                    }}
                    loading={loadingUniversities}
                    sx={{ flex: 1, maxWidth: "350px" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search or enter institute name"
                        size="small"
                        error={attemptedSubmit && !isInstituteNameValid()}
                        helperText={
                          attemptedSubmit && !isInstituteNameValid()
                            ? "Institute name is required"
                            : ""
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingUniversities ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} style={{ fontSize: "13px" }}>
                        <Box>
                          <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                          <Box sx={{ fontSize: "11px", color: "#6B7280" }}>
                            {option.type}
                            {option.state && `, ${option.state}`}
                            {option.country && ` - ${option.country}`}
                          </Box>
                        </Box>
                      </li>
                    )}
                  />
                </FormField>

                <FormField>
                  <FieldLabel required>Institute Type</FieldLabel>
                  <Autocomplete
                    value={instituteType}
                    onChange={(event, newValue) => {
                      if (newValue === "Create New") {
                        handleOpenDialog();
                      } else {
                        setInstituteType(newValue);
                      }
                    }}
                    options={[...currentInstituteTypes, "Create New"]}
                    sx={{ flex: 1, maxWidth: "350px" }}
                    renderOption={(props, option) => {
                      if (option === "Create New") {
                        return null;
                      }
                      return (
                        <li {...props} style={{ fontSize: "13px" }}>
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select institute type"
                        size="small"
                        error={attemptedSubmit && !isInstituteTypeValid()}
                        helperText={
                          attemptedSubmit && !isInstituteTypeValid()
                            ? "Institute type is required"
                            : ""
                        }
                      />
                    )}
                  />
                </FormField>
                <FormField sx={{ alignItems: "center" }}>
                  <FieldLabel>Institute Logo</FieldLabel>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {logoPreview ? (
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            border: "1px solid #E5E7EB",
                            borderRadius: "6px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            border: "1px dashed #D1D5DB",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#F9FAFB",
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 28, color: "#D1D5DB" }} />
                        </Box>
                      )}

                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          component="label"
                          sx={{
                            borderColor: "#D1D5DB",
                            color: "#4B5563",
                            fontSize: "12px",
                            "&:hover": { borderColor: "#9CA3AF", backgroundColor: "#F9FAFB" },
                          }}
                        >
                          {logoPreview ? "Change Logo" : "Upload Logo"}
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </Button>

                        {logoPreview && (
                          <Button
                            size="small"
                            onClick={handleRemoveLogo}
                            sx={{ ml: 1, color: "#E85858", fontSize: "12px" }}
                          >
                            Remove
                          </Button>
                        )}

                        <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.5 }}>
                          Max size: 1MB. PNG, JPG, SVG accepted.
                        </Typography>
                      </Box>
                    </Box>
                  </FormField>
                  <FormField>
                        <FieldLabel>Student Hold (due to TDS)</FieldLabel>
                        <TextField
                          value={studentHoldTds}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setStudentHoldTds(value);
                          }}
                          type="number"
                          size="small"
                          inputProps={{ min: 0 }}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                          placeholder="Enter value"
                        />
                      </FormField>
                {/* <FormField>
                  <FieldLabel>University Name</FieldLabel>
                  <TextField
                    value={universityName}
                    onChange={(e) => setUniversityName(e.target.value)}
                    placeholder="Enter university name (optional)"
                    size="small"
                    sx={{ flex: 1, maxWidth: "350px" }}
                    autoComplete="off"
                  />
                </FormField> */}
              </Box>

              <Tabs value={mainTab} onChange={handleMainTabChange}>
                <Tab label="Addresses & Other Details" />
              </Tabs>

              {mainTab === 0 && (
                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          mb: 2,
                          color: "#1F1F1F",
                        }}
                      >
                        Billing Address
                      </Typography>

                      <FormField>
                        <FieldLabel>Attention</FieldLabel>
                        <TextField
                          value={billingAttention}
                          onChange={(e) => setBillingAttention(e.target.value)}
                          size="small"
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel required>Country/Region</FieldLabel>
                        <Autocomplete
                          value={billingCountry}
                          onChange={(event, newValue) => {
                            setBillingCountry(newValue);
                            if (!newValue) {
                              setBillingState(null);
                              setBillingCity(null);
                              setBillingPhone("");
                              setBillingFax("");
                            }
                          }}
                          options={countries}
                          getOptionLabel={(option) => option.country_name || ""}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontSize: "13px" }}>
                              {option.country_name}
                            </li>
                          )}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select country"
                              size="small"
                              error={attemptedSubmit && !isCountryValid()}
                              helperText={
                                attemptedSubmit && !isCountryValid()
                                  ? "Country is required"
                                  : ""
                              }
                            />
                          )}
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel required>State</FieldLabel>
                        <Autocomplete
                          value={billingState}
                          onChange={(event, newValue) => {
                            setBillingState(newValue);
                            if (!newValue) {
                              setBillingCity(null);
                            }
                          }}
                          options={billingStates}
                          getOptionLabel={(option) => option.state_name || ""}
                          disabled={!billingCountry}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontSize: "13px" }}>
                              {option.state_name}
                            </li>
                          )}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                billingCountry
                                  ? "Select state"
                                  : "Select country first"
                              }
                              size="small"
                              error={attemptedSubmit && !isStateValid()}
                              helperText={
                                attemptedSubmit && !isStateValid()
                                  ? "State is required"
                                  : ""
                              }
                            />
                          )}
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel required>City</FieldLabel>
                        <Autocomplete
                          freeSolo
                          value={billingCity}
                          onChange={handleCityChange}
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                              setBillingCity(newInputValue);
                            }
                          }}
                          options={billingCities}
                          getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            return option?.name || "";
                          }}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options.slice(0, 50);
                            return options
                              .filter((option) =>
                                option.name
                                  .toLowerCase()
                                  .includes(inputValue.toLowerCase())
                              )
                              .slice(0, 50);
                          }}
                          disabled={!billingState}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                billingState
                                  ? "Enter or select city"
                                  : "Select state first"
                              }
                              size="small"
                              error={attemptedSubmit && !isCityValid()}
                              helperText={
                                attemptedSubmit && !isCityValid()
                                  ? "City is required"
                                  : ""
                              }
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} style={{ fontSize: "13px" }}>
                              {option.name}
                            </li>
                          )}
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel required>Address</FieldLabel>
                        <TextField
                          value={billingAddressLine1}
                          onChange={(e) =>
                            setBillingAddressLine1(e.target.value)
                          }
                          placeholder="Street 1"
                          multiline
                          rows={2}
                          maxRows={4}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                          error={attemptedSubmit && !isAddressLine1Valid()}
                          helperText={
                            attemptedSubmit && !isAddressLine1Valid()
                              ? "Address is required"
                              : ""
                          }
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel width="140px"></FieldLabel>
                        <TextField
                          value={billingAddressLine2}
                          onChange={(e) =>
                            setBillingAddressLine2(e.target.value)
                          }
                          placeholder="Street 2"
                          multiline
                          rows={2}
                          maxRows={4}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel required>Pin Code</FieldLabel>
                        <TextField
                          value={billingPincode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 10) {
                              setBillingPincode(value);
                            }
                          }}
                          size="small"
                          inputProps={{ maxLength: 10 }}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                          error={attemptedSubmit && !isPincodeValid()}
                          helperText={
                            attemptedSubmit && !isPincodeValid()
                              ? "Pin code is required"
                              : ""
                          }
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel>Phone</FieldLabel>
                        <TextField
                          value={billingPhone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setBillingPhone(value);
                          }}
                          placeholder={
                            billingCountry
                              ? "Enter phone number"
                              : "Select country first"
                          }
                          size="small"
                          disabled={!billingCountry}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                          InputProps={{
                            startAdornment: billingCountry && (
                              <InputAdornment position="start">
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "#6B7280",
                                    fontWeight: 500,
                                  }}
                                >
                                  {getPhoneCode()}
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel>Fax Number</FieldLabel>
                        <TextField
                          value={billingFax}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setBillingFax(value);
                          }}
                          placeholder={
                            billingCountry
                              ? "Enter fax number"
                              : "Select country first"
                          }
                          size="small"
                          disabled={!billingCountry}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                          InputProps={{
                            startAdornment: billingCountry && (
                              <InputAdornment position="start">
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    color: "#6B7280",
                                    fontWeight: 500,
                                  }}
                                >
                                  {getPhoneCode()}
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </FormField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          mb: 2,
                          color: "#1F1F1F",
                        }}
                      >
                        Other Details
                      </Typography>

                      <FormField>
                        <FieldLabel>Institute Website</FieldLabel>
                        <TextField
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          type="url"
                          size="small"
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                        />
                      </FormField>

                      <FormField>
                        <FieldLabel>GSTIN No.</FieldLabel>
                        <TextField
                          value={gstinNo}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            if (value.length <= 15) {
                              setGstinNo(value);
                            }
                          }}
                          size="small"
                          inputProps={{ maxLength: 15 }}
                          sx={{ flex: 1, maxWidth: "300px" }}
                          autoComplete="off"
                        />
                      </FormField>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          )}
        </Box>

        {/* Fixed Footer for Create View */}
        {currentView === "create" && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#FFFFFF",
              borderTop: "1px solid #E5E7EB",
              py: 2,
              px: 3,
              zIndex: 1000,
              boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Box
              sx={{
                maxWidth: "1400px",
                margin: "0 auto",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBackToList}
                disabled={isSubmitting}
                sx={{
                  borderColor: "#D1D5DB",
                  color: "#6B7280",
                  "&:hover": {
                    borderColor: "#9CA3AF",
                    backgroundColor: "#F9FAFB",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  backgroundColor: "#3B82F6",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#2563EB",
                  },
                  "&:disabled": {
                    backgroundColor: "#E5E7EB",
                    color: "#9CA3AF",
                  },
                  px: 2.5,
                }}
              >
                {isSubmitting ? "Adding..." : "Add Institute"}
              </Button>
            </Box>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
            Create New Institute Type
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              value={newInstituteType}
              onChange={(e) => setNewInstituteType(e.target.value)}
              placeholder="Enter new institute type"
              sx={{ mt: 2 }}
              autoComplete="off"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ color: "#6B7280" }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewType}
              variant="contained"
              disabled={!newInstituteType.trim()}
              sx={{
                backgroundColor: "#3B82F6",
                "&:hover": { backgroundColor: "#2563EB" },
              }}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default InstituteManagement;
